import { useEffect, useState } from 'react';
import { pagoService, prestamoService } from '../../services/api';
import ModalAccionesPago from '../../components/ModalAccionesPago';
import '../../styles/Pagos.css';

const PagoFacil = () => {
  const [prestamos, setPrestamos] = useState([]);
  const [filtro, setFiltro] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(false);
  const [modalPago, setModalPago] = useState(null);
  const [modalConfirmacion, setModalConfirmacion] = useState(null);
  const [modalExito, setModalExito] = useState(false);
  const [pagarCapital, setPagarCapital] = useState(true);
  const [pagarInteres, setPagarInteres] = useState(true);
  // Estados para el modal de acciones después del pago
  const [mostrarModalAcciones, setMostrarModalAcciones] = useState(false);
  const [datosPagoGuardado, setDatosPagoGuardado] = useState(null);

  useEffect(() => {
    cargarPrestamos();
  }, []);

  const cargarPrestamos = async () => {
    setCargando(true);
    try {
      // Solicitar los primeros 200 préstamos para obtener todos los activos
      const response = await prestamoService.getAll({ per_page: 200 });
      const prestamosActivos = (response.data.prestamos || []).filter(
        p => p.estado === 'activo'
      );
      setPrestamos(prestamosActivos);
    } catch (error) {
      console.error('Error al cargar préstamos:', error);
    } finally {
      setCargando(false);
    }
  };

  const calcularEstadoPago = (prestamo) => {
    const cuotasPendientes = prestamo.cuotas - prestamo.cuotas_pagadas;
    const porcentajePagado = (prestamo.cuotas_pagadas / prestamo.cuotas) * 100;

    if (porcentajePagado === 0) return 'sin-pagar';
    if (porcentajePagado < 30) return 'bajo';
    if (porcentajePagado < 70) return 'medio';
    if (porcentajePagado < 100) return 'alto';
    return 'completo';
  };

  const registrarPagoRapido = async (prestamo) => {
    // Abrir modal en lugar de confirmar directamente
    setModalPago(prestamo);
    setPagarCapital(true);
    setPagarInteres(true);
  };

  const confirmarPago = async () => {
    if (!modalPago) return;

    // Validar que al menos una opción esté seleccionada
    if (!pagarCapital && !pagarInteres) {
      alert('Debe seleccionar al menos Capital o Interés');
      return;
    }

    const capitalCuota = modalPago.capital / modalPago.cuotas;
    const interesCuota = modalPago.interes / modalPago.cuotas;

    const montoCapital = pagarCapital ? capitalCuota : 0;
    const montoInteres = pagarInteres ? interesCuota : 0;
    const montoTotal = montoCapital + montoInteres;

    // Mostrar modal de confirmación
    setModalConfirmacion({
      prestamo: modalPago,
      montoCapital,
      montoInteres,
      montoTotal,
      detalle: pagarCapital && pagarInteres ? 'Pago completo' : pagarCapital ? 'Pago de capital' : 'Pago de interés'
    });
  };

  const ejecutarPago = async () => {
    if (!modalConfirmacion) return;

    // Guardar datos para el modal de acciones
    const nombreCliente = modalConfirmacion.prestamo.cliente ? 
      `${modalConfirmacion.prestamo.cliente.nombres} ${modalConfirmacion.prestamo.cliente.apellidos}` : 
      'Cliente';

    try {
      const response = await pagoService.create({
        prestamo_id: modalConfirmacion.prestamo.id,
        monto_capital: modalConfirmacion.montoCapital,
        monto_interes: modalConfirmacion.montoInteres,
        monto_mora: 0,
        monto_total: modalConfirmacion.montoTotal,
        tipo_pago: 'normal',
        fecha_pago: new Date().toISOString().split('T')[0]
      });

      // Cerrar modales previos
      setModalConfirmacion(null);
      setModalPago(null);
      setModalExito(true);

      // Guardar datos para el modal de acciones
      setDatosPagoGuardado({
        nombreCliente,
        prestamoId: modalConfirmacion.prestamo.id,
        montoPagado: modalConfirmacion.montoTotal,
        cuotasPagadas: modalConfirmacion.prestamo.cuotas_pagadas + 1,
        totalCuotas: modalConfirmacion.prestamo.cuotas,
        pagoId: response.data.id,
        cliente: modalConfirmacion.prestamo.cliente,
        prestamo: modalConfirmacion.prestamo
      });

      // Mostrar modal de acciones
      setMostrarModalAcciones(true);

      // Recargar préstamos
      cargarPrestamos();
    } catch (error) {
      console.error('Error completo:', error);
      let mensaje = 'Error al registrar el pago';
      if (error.response) {
        mensaje += `: ${error.response.data?.error || error.response.statusText}`;
      } else if (error.request) {
        mensaje += ': No se pudo conectar con el servidor. Verifica tu conexión.';
      } else {
        mensaje += `: ${error.message}`;
      }

      alert(mensaje);
    }
  };

  const debePagarHoy = (prestamo) => {
    // Excluir préstamos de capital final
    if (prestamo.tipo === 'capital_final' || prestamo.tipo === 'capital_final_fijo') {
      return false;
    }

    // Excluir si ya completó todas las cuotas
    if (prestamo.cuotas_pagadas >= prestamo.cuotas) {
      return false;
    }

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const fechaInicio = new Date(prestamo.fecha_inicio);
    const fechaEfectividad = new Date(prestamo.fecha_efectividad);
    fechaInicio.setHours(0, 0, 0, 0);
    fechaEfectividad.setHours(0, 0, 0, 0);

    // Para préstamos DIARIOS: desde fecha_efectividad
    // IMPORTANTE: Solo si NO ha pagado hoy (días transcurridos >= cuotas pagadas)
    if (prestamo.modalidad === 'Diario') {
      const diasDesdeEfectividad = Math.floor((hoy - fechaEfectividad) / (1000 * 60 * 60 * 24));
      return fechaEfectividad <= hoy && diasDesdeEfectividad >= prestamo.cuotas_pagadas;
    }

    // Para otros tipos: calcular según ciclo desde fecha_inicio
    if (fechaInicio > hoy) {
      return false;
    }

    const diasDesdeInicio = Math.floor((hoy - fechaInicio) / (1000 * 60 * 60 * 24));

    if (prestamo.modalidad === 'Semanal') {
      return diasDesdeInicio % 7 === 0;
    } else if (prestamo.modalidad === 'Quincenal') {
      return diasDesdeInicio % 15 === 0;
    } else if (prestamo.modalidad === 'Mensual') {
      return diasDesdeInicio % 30 === 0;
    }

    return false;
  };

  const prestamosFiltrados = prestamos.filter(prestamo => {
    const estado = calcularEstadoPago(prestamo);

    // Si el filtro es "deben-pagar-hoy", aplicar lógica especial
    if (filtro === 'deben-pagar-hoy') {
      const cumpleFiltro = debePagarHoy(prestamo);

      // Búsqueda
      const terminoBusqueda = busqueda.toLowerCase().trim();
      const cumpleBusqueda = !terminoBusqueda ||
        prestamo.id.toString().includes(terminoBusqueda) ||
        prestamo.cliente_id.toString().includes(terminoBusqueda) ||
        (prestamo.cliente?.nombres && prestamo.cliente.nombres.toLowerCase().includes(terminoBusqueda)) ||
        (prestamo.cliente?.apellidos && prestamo.cliente.apellidos.toLowerCase().includes(terminoBusqueda)) ||
        (prestamo.cliente?.cedula && prestamo.cliente.cedula.includes(terminoBusqueda)) ||
        (prestamo.cliente?.apodo && prestamo.cliente.apodo.toLowerCase().includes(terminoBusqueda)) ||
        (prestamo.cliente?.nombres && prestamo.cliente?.apellidos &&
         `${prestamo.cliente.nombres} ${prestamo.cliente.apellidos}`.toLowerCase().includes(terminoBusqueda));

      return cumpleFiltro && cumpleBusqueda;
    }

    // Para otros filtros, usar lógica normal
    const cumpleFiltro = filtro === 'todos' || estado === filtro;

    // Búsqueda mejorada: por ID, cliente, cédula, nombres, apellidos y apodo
    const terminoBusqueda = busqueda.toLowerCase().trim();
    const cumpleBusqueda = !terminoBusqueda ||
      prestamo.id.toString().includes(terminoBusqueda) ||
      prestamo.cliente_id.toString().includes(terminoBusqueda) ||
      (prestamo.cliente?.nombres && prestamo.cliente.nombres.toLowerCase().includes(terminoBusqueda)) ||
      (prestamo.cliente?.apellidos && prestamo.cliente.apellidos.toLowerCase().includes(terminoBusqueda)) ||
      (prestamo.cliente?.cedula && prestamo.cliente.cedula.includes(terminoBusqueda)) ||
      (prestamo.cliente?.apodo && prestamo.cliente.apodo.toLowerCase().includes(terminoBusqueda)) ||
      // Búsqueda por nombre completo
      (prestamo.cliente?.nombres && prestamo.cliente?.apellidos &&
       `${prestamo.cliente.nombres} ${prestamo.cliente.apellidos}`.toLowerCase().includes(terminoBusqueda));

    return cumpleFiltro && cumpleBusqueda;
  });

  const handleCerrarModalAcciones = () => {
    setMostrarModalAcciones(false);
    setModalExito(false);
    setDatosPagoGuardado(null);
  };

  const handleImprimirRecibo = () => {
    if (datosPagoGuardado) {
      window.open(`/recibo/${datosPagoGuardado.pagoId}`, '_blank');
    }
  };

  const handleEnviarWhatsApp = () => {
    if (datosPagoGuardado && datosPagoGuardado.cliente) {
      const celular = datosPagoGuardado.cliente.celular;
      if (!celular) {
        alert('❌ El cliente no tiene número de celular registrado');
        return;
      }

      const mensaje = 
        `✅ *Pago Registrado*\n\n` +
        `Cliente: ${datosPagoGuardado.nombreCliente}\n` +
        `Préstamo ID: ${datosPagoGuardado.prestamoId}\n` +
        `Monto pagado: $${datosPagoGuardado.montoPagado.toFixed(2)}\n` +
        `Cuotas pagadas: ${datosPagoGuardado.cuotasPagadas}/${datosPagoGuardado.totalCuotas}\n\n` +
        `¡Gracias por su pago!`;

      const numeroLimpio = celular.replace(/\D/g, '');
      const numeroWhatsApp = numeroLimpio.startsWith('1') ? numeroLimpio : `1${numeroLimpio}`;
      
      window.open(
        `https://web.whatsapp.com/send?phone=${numeroWhatsApp}&text=${encodeURIComponent(mensaje)}`,
        '_blank'
      );
    }
  };

  return (
    <div className="pago-facil-container">
      <h2>Pago Fácil</h2>

      <div className="controles">
        <div className="filtros">
          <button
            className={filtro === 'deben-pagar-hoy' ? 'active' : ''}
            onClick={() => setFiltro('deben-pagar-hoy')}
            style={{
              backgroundColor: filtro === 'deben-pagar-hoy' ? '#e67e22' : '#f39c12',
              color: '#fff',
              fontWeight: 'bold',
              border: filtro === 'deben-pagar-hoy' ? '2px solid #d35400' : 'none'
            }}
          >
            Deben Pagar Hoy
          </button>
          <button
            className={filtro === 'todos' ? 'active' : ''}
            onClick={() => setFiltro('todos')}
          >
            Todos ({prestamos.length})
          </button>
          <button
            className={filtro === 'sin-pagar' ? 'active sin-pagar' : 'sin-pagar'}
            onClick={() => setFiltro('sin-pagar')}
          >
            Sin Pagar
          </button>
          <button
            className={filtro === 'bajo' ? 'active bajo' : 'bajo'}
            onClick={() => setFiltro('bajo')}
          >
            Bajo
          </button>
          <button
            className={filtro === 'medio' ? 'active medio' : 'medio'}
            onClick={() => setFiltro('medio')}
          >
            Medio
          </button>
          <button
            className={filtro === 'alto' ? 'active alto' : 'alto'}
            onClick={() => setFiltro('alto')}
          >
            Alto
          </button>
          <button
            className={filtro === 'completo' ? 'active completo' : 'completo'}
            onClick={() => setFiltro('completo')}
          >
            Completo
          </button>
        </div>

        <div className="busqueda">
          <input
            type="text"
            placeholder="Buscar por nombre, cédula, apodo o ID..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              fontSize: '1rem',
              border: '2px solid #ddd',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#2196F3'}
            onBlur={(e) => e.target.style.borderColor = '#ddd'}
          />
          {busqueda && prestamosFiltrados.length > 0 && (
            <div style={{
              marginTop: '0.5rem',
              fontSize: '0.9rem',
              color: '#666'
            }}>
              {prestamosFiltrados.length} resultado{prestamosFiltrados.length !== 1 ? 's' : ''} encontrado{prestamosFiltrados.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {cargando ? (
        <div className="cargando">Cargando préstamos...</div>
      ) : (
        <div className="prestamos-grid">
          {prestamosFiltrados.map(prestamo => {
            const estado = calcularEstadoPago(prestamo);
            const porcentaje = (prestamo.cuotas_pagadas / prestamo.cuotas) * 100;
            const montoCuota = prestamo.total / prestamo.cuotas;

            return (
              <div key={prestamo.id} className={`prestamo-card ${estado}`}>
                <div className="card-header">
                  <span className="prestamo-id">ID: {prestamo.id}</span>
                  <span className={`estado-badge ${estado}`}>
                    {estado.replace('-', ' ').toUpperCase()}
                  </span>
                </div>

                <div className="card-body">
                  {/* Foto y nombre del cliente */}
                  {prestamo.cliente && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      marginBottom: '1rem',
                      padding: '0.75rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      borderRadius: '8px'
                    }}>
                      {prestamo.cliente.foto_perfil ? (
                        <img
                          src={prestamo.cliente.foto_perfil}
                          alt={`${prestamo.cliente.nombres} ${prestamo.cliente.apellidos}`}
                          style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '3px solid #fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1.5rem',
                          border: '3px solid #fff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          {prestamo.cliente.nombres?.[0] || 'C'}{prestamo.cliente.apellidos?.[0] || ''}
                        </div>
                      )}
                      <div style={{flex: 1}}>
                        <div style={{fontWeight: 'bold', fontSize: '1rem', marginBottom: '0.25rem'}}>
                          {prestamo.cliente.nombres} {prestamo.cliente.apellidos}
                        </div>
                        {prestamo.cliente.apodo && (
                          <div style={{
                            fontSize: '0.85rem',
                            color: '#2196F3',
                            fontStyle: 'italic',
                            marginBottom: '0.25rem'
                          }}>
                            "{prestamo.cliente.apodo}"
                          </div>
                        )}
                        <div style={{fontSize: '0.85rem', color: '#666'}}>
                          {prestamo.cliente.cedula || 'Sin cédula'}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="info-row">
                    <strong>Cliente ID:</strong> {prestamo.cliente_id}
                  </div>
                  <div className="info-row">
                    <strong>Total:</strong> ${prestamo.total.toFixed(2)}
                  </div>
                  <div className="info-row">
                    <strong>Cuota:</strong> ${montoCuota.toFixed(2)}
                  </div>
                  <div className="info-row">
                    <strong>Cuotas:</strong> {prestamo.cuotas_pagadas}/{prestamo.cuotas}
                  </div>

                  <div className="progreso">
                    <div className="progreso-bar">
                      <div
                        className={`progreso-fill ${estado}`}
                        style={{ width: `${porcentaje}%` }}
                      />
                    </div>
                    <span className="progreso-texto">{porcentaje.toFixed(0)}%</span>
                  </div>
                </div>

                <div className="card-footer">
                  <button
                    className="btn-pagar"
                    onClick={() => registrarPagoRapido(prestamo)}
                    disabled={prestamo.cuotas_pagadas >= prestamo.cuotas}
                  >
                    Registrar Pago
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {prestamosFiltrados.length === 0 && !cargando && (
        <div className="sin-resultados">
          No se encontraron préstamos con los criterios seleccionados
        </div>
      )}

      {/* Modal de opciones de pago */}
      {modalPago && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{marginTop: 0, marginBottom: '1.5rem', color: '#333'}}>
              Opciones de Pago - Préstamo #{modalPago.id}
            </h3>

            {/* Información del cliente */}
            {modalPago.cliente && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                {modalPago.cliente.foto_perfil ? (
                  <img
                    src={modalPago.cliente.foto_perfil}
                    alt={`${modalPago.cliente.nombres} ${modalPago.cliente.apellidos}`}
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '2px solid #fff'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    border: '2px solid #fff'
                  }}>
                    {modalPago.cliente.nombres?.[0] || 'C'}{modalPago.cliente.apellidos?.[0] || ''}
                  </div>
                )}
                <div>
                  <div style={{fontWeight: 'bold'}}>
                    {modalPago.cliente.nombres} {modalPago.cliente.apellidos}
                  </div>
                  {modalPago.cliente.apodo && (
                    <div style={{fontSize: '0.85rem', color: '#2196F3', fontStyle: 'italic'}}>
                      "{modalPago.cliente.apodo}"
                    </div>
                  )}
                  <div style={{fontSize: '0.85rem', color: '#666'}}>
                    {modalPago.cliente.cedula || 'Sin cédula'}
                  </div>
                </div>
              </div>
            )}

            {/* Opciones de pago */}
            <div style={{marginBottom: '1.5rem'}}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem',
                marginBottom: '0.5rem',
                backgroundColor: pagarCapital ? '#e3f2fd' : '#f5f5f5',
                borderRadius: '8px',
                cursor: 'pointer',
                border: pagarCapital ? '2px solid #2196F3' : '2px solid #ddd',
                transition: 'all 0.2s'
              }}>
                <input
                  type="checkbox"
                  checked={pagarCapital}
                  onChange={(e) => setPagarCapital(e.target.checked)}
                  style={{
                    width: '20px',
                    height: '20px',
                    marginRight: '1rem',
                    cursor: 'pointer'
                  }}
                />
                <div style={{flex: 1}}>
                  <div style={{fontWeight: 'bold', color: '#333'}}>Capital</div>
                  <div style={{fontSize: '0.9rem', color: '#666'}}>
                    DOP {(modalPago.capital / modalPago.cuotas).toFixed(2)}
                  </div>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem',
                backgroundColor: pagarInteres ? '#e8f5e9' : '#f5f5f5',
                borderRadius: '8px',
                cursor: 'pointer',
                border: pagarInteres ? '2px solid #4CAF50' : '2px solid #ddd',
                transition: 'all 0.2s'
              }}>
                <input
                  type="checkbox"
                  checked={pagarInteres}
                  onChange={(e) => setPagarInteres(e.target.checked)}
                  style={{
                    width: '20px',
                    height: '20px',
                    marginRight: '1rem',
                    cursor: 'pointer'
                  }}
                />
                <div style={{flex: 1}}>
                  <div style={{fontWeight: 'bold', color: '#333'}}>Interés</div>
                  <div style={{fontSize: '0.9rem', color: '#666'}}>
                    DOP {(modalPago.interes / modalPago.cuotas).toFixed(2)}
                  </div>
                </div>
              </label>
            </div>

            {/* Total a pagar */}
            <div style={{
              padding: '1rem',
              backgroundColor: '#fff3e0',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '2px solid #ff9800'
            }}>
              <div style={{fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem'}}>
                Total a pagar:
              </div>
              <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#f57c00'}}>
                DOP {(
                  (pagarCapital ? modalPago.capital / modalPago.cuotas : 0) +
                  (pagarInteres ? modalPago.interes / modalPago.cuotas : 0)
                ).toFixed(2)}
              </div>
            </div>

            {/* Botones */}
            <div style={{display: 'flex', gap: '1rem'}}>
              <button
                onClick={() => setModalPago(null)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: '2px solid #ddd',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarPago}
                disabled={!pagarCapital && !pagarInteres}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: 'none',
                  backgroundColor: (!pagarCapital && !pagarInteres) ? '#ccc' : '#4CAF50',
                  color: 'white',
                  borderRadius: '8px',
                  cursor: (!pagarCapital && !pagarInteres) ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}
              >
                Confirmar Pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación */}
      {modalConfirmacion && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            {/* Icono de advertencia */}
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1.5rem',
              borderRadius: '50%',
              border: '4px solid #ff9800',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{fontSize: '3rem', color: '#ff9800'}}>!</span>
            </div>

            <h2 style={{margin: '0 0 1rem', color: '#333'}}>Pago Fácil</h2>

            <p style={{color: '#d32f2f', fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '1.5rem'}}>
              ¿Está seguro que desea realizar el pago para el préstamo seleccionado?
            </p>

            <div style={{
              backgroundColor: '#f5f5f5',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              textAlign: 'left'
            }}>
              <div style={{marginBottom: '0.5rem'}}>
                <strong>Cliente:</strong> {modalConfirmacion.prestamo.cliente?.nombres} {modalConfirmacion.prestamo.cliente?.apellidos}
              </div>
              <div style={{marginBottom: '0.5rem'}}>
                <strong>Monto:</strong> ${Math.round(modalConfirmacion.montoTotal)}
              </div>
              <div>
                <strong>Detalle:</strong> {modalConfirmacion.detalle}
              </div>
            </div>

            <div style={{display: 'flex', gap: '1rem', justifyContent: 'center'}}>
              <button
                onClick={() => setModalConfirmacion(null)}
                style={{
                  padding: '0.75rem 2rem',
                  border: 'none',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}
              >
                No, cancelar
              </button>
              <button
                onClick={ejecutarPago}
                style={{
                  padding: '0.75rem 2rem',
                  border: 'none',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}
              >
                Cobrar
              </button>
              <button
                onClick={() => {
                  setModalConfirmacion(null);
                  // TODO: Implementar impresión
                }}
                style={{
                  padding: '0.75rem 2rem',
                  border: 'none',
                  backgroundColor: '#f44336',
                  color: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}
              >
                Cobrar e Imprimir
              </button>
            </div>

            <div style={{
              marginTop: '1.5rem',
              padding: '0.75rem',
              backgroundColor: '#e3f2fd',
              borderRadius: '8px',
              fontSize: '0.9rem',
              color: '#1976d2'
            }}>
              <strong>Préstamo ID:</strong> {modalConfirmacion.prestamo.id} ,
              <strong> Modalidad:</strong> {modalConfirmacion.prestamo.modalidad} ,
              <strong> Tipo:</strong> {modalConfirmacion.prestamo.tipo}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Éxito */}
      {modalExito && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            {/* Icono de éxito */}
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1.5rem',
              borderRadius: '50%',
              border: '4px solid #4CAF50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{fontSize: '3rem', color: '#4CAF50'}}>✓</span>
            </div>

            <h2 style={{margin: '0 0 1rem', color: '#333'}}>Pago Fácil</h2>

            <p style={{fontSize: '1.1rem', marginBottom: '1.5rem', color: '#4CAF50'}}>
              El pago del interés se ha completado con éxito.
            </p>

            <button
              onClick={() => setModalExito(false)}
              style={{
                padding: '0.75rem 3rem',
                border: 'none',
                backgroundColor: '#673AB7',
                color: 'white',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '1rem'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Modal de acciones después del pago */}
      <ModalAccionesPago
        mostrar={mostrarModalAcciones}
        onCerrar={handleCerrarModalAcciones}
        pagoInfo={datosPagoGuardado}
        onImprimirRecibo={handleImprimirRecibo}
        onEnviarWhatsApp={handleEnviarWhatsApp}
      />
    </div>
  );
};

export default PagoFacil;
